import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { API, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { StatusBadge, SalesBadge } from "@/components/common/StatusBadge";
import CorporateDetailModal from "@/components/corporates/CorporateDetailModal";
import { Plus, Search, Download, Trash2, Pencil, Eye, Loader2, FileSpreadsheet, FileText, Building2 } from "lucide-react";

export default function CorporateList({ properties, reloadNotifications }) {
  const { isManagement } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [states, setStates] = useState([]);
  const [filters, setFilters] = useState({ q: "", state: "", district: "", contract: "", tariff: "", sales: "" });
  const [districts, setDistricts] = useState([]);
  const [modal, setModal] = useState({ open: false, id: null, create: false });
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/corporates", { params }).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/locations/states").then((r) => setStates(r.data.states)); }, []);
  useEffect(() => {
    if (filters.state) api.get("/locations/districts", { params: { state: filters.state } }).then((r) => setDistricts(r.data.districts));
    else setDistricts([]);
  }, [filters.state]);

  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v === "__all" ? "" : v, ...(k === "state" ? { district: "" } : {}) }));

  const doExport = async (fmt) => {
    try {
      const params = {};
      if (filters.state) params.state = filters.state;
      if (filters.district) params.district = filters.district;
      const res = await api.get(`/corporates/export/${fmt}`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `corporates.${fmt === "excel" ? "xlsx" : fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/corporates/${toDelete.id}`);
      toast.success("Corporate deleted");
      setToDelete(null);
      load();
      reloadNotifications();
    } catch (e) { toast.error(apiError(e)); }
  };

  const statSummary = {
    total: rows.length,
    pending: rows.filter((r) => r.tariff_status === "Pending").length,
    contracted: rows.filter((r) => r.contract_status === "Contracted").length,
    nonContracted: rows.filter((r) => r.contract_status === "Non-Contracted").length,
    noSales: rows.filter((r) => !r.sales_status).length,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-serif font-semibold tracking-tight">Corporate Master</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isManagement ? "All corporate accounts across every property." : "Corporate accounts in your assigned region."}
          </p>
        </div>
        <div className="flex gap-2">
          {isManagement && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="export-menu-button"><Download className="h-4 w-4 mr-2" /> Export</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => doExport("csv")} data-testid="export-csv-button"><FileText className="h-4 w-4 mr-2" /> CSV</DropdownMenuItem>
                <DropdownMenuItem onClick={() => doExport("excel")} data-testid="export-excel-button"><FileSpreadsheet className="h-4 w-4 mr-2" /> Excel</DropdownMenuItem>
                <DropdownMenuItem onClick={() => doExport("pdf")} data-testid="export-pdf-button"><FileText className="h-4 w-4 mr-2" /> PDF</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button onClick={() => setModal({ open: true, id: null, create: true })} data-testid="add-corporate-button">
            <Plus className="h-4 w-4 mr-2" /> Add Corporate
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {[
          { l: "Total Corporates", v: statSummary.total },
          { l: "Contracted", v: statSummary.contracted },
          { l: "Non-Contracted", v: statSummary.nonContracted },
          { l: "Pending Tariffs", v: statSummary.pending },
          { l: "No Sales (Month)", v: statSummary.noSales },
        ].map((s) => (
          <div key={s.l} className="rounded-xl border border-border bg-card p-4">
            <div className="text-2xl font-mono font-bold text-primary">{s.v}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide mt-1">{s.l}</div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-border bg-card p-3 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={filters.q} onChange={(e) => setF("q", e.target.value)} placeholder="Search company, POC, GST, email…" className="pl-9" data-testid="global-search-input" />
        </div>
        <Select value={filters.state || "__all"} onValueChange={(v) => setF("state", v)}>
          <SelectTrigger className="w-[150px]" data-testid="state-filter-select"><SelectValue placeholder="State" /></SelectTrigger>
          <SelectContent><SelectItem value="__all">All States</SelectItem>{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.district || "__all"} onValueChange={(v) => setF("district", v)} disabled={!filters.state}>
          <SelectTrigger className="w-[150px]" data-testid="city-filter-select"><SelectValue placeholder="District" /></SelectTrigger>
          <SelectContent><SelectItem value="__all">All Districts</SelectItem>{districts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.contract || "__all"} onValueChange={(v) => setF("contract", v)}>
          <SelectTrigger className="w-[160px]" data-testid="status-filter-select"><SelectValue placeholder="Contract" /></SelectTrigger>
          <SelectContent><SelectItem value="__all">All Contracts</SelectItem><SelectItem value="Contracted">Contracted</SelectItem><SelectItem value="Non-Contracted">Non-Contracted</SelectItem></SelectContent>
        </Select>
        <Select value={filters.tariff || "__all"} onValueChange={(v) => setF("tariff", v)}>
          <SelectTrigger className="w-[150px]" data-testid="tariff-filter-select"><SelectValue placeholder="Tariff" /></SelectTrigger>
          <SelectContent><SelectItem value="__all">All Tariffs</SelectItem><SelectItem value="Approved">Approved</SelectItem><SelectItem value="Pending">Pending</SelectItem><SelectItem value="Rejected">Rejected</SelectItem><SelectItem value="Draft">Draft</SelectItem></SelectContent>
        </Select>
        <Select value={filters.sales || "__all"} onValueChange={(v) => setF("sales", v)}>
          <SelectTrigger className="w-[140px]" data-testid="sales-active-filter-toggle"><SelectValue placeholder="Sales" /></SelectTrigger>
          <SelectContent><SelectItem value="__all">All Sales</SelectItem><SelectItem value="yes">Has Sales</SelectItem><SelectItem value="no">No Sales</SelectItem></SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="corporate-master-table">
            <TableHeader>
              <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                <TableHead>Company</TableHead>
                <TableHead>State</TableHead>
                <TableHead>District</TableHead>
                <TableHead>Area</TableHead>
                <TableHead>POC Contact</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Contract</TableHead>
                <TableHead>Sales ({(rows[0]?.sales_month || "Month")})</TableHead>
                <TableHead>Tariff</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} className="h-40 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-primary" /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="h-40 text-center text-muted-foreground">
                  <Building2 className="h-8 w-8 mx-auto mb-2 opacity-40" /> No corporates found.
                </TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id} data-testid={`corporate-row-${r.id}`} className="cursor-pointer" onClick={() => setModal({ open: true, id: r.id, create: false })}>
                  <TableCell className="font-medium">{r.company_name}</TableCell>
                  <TableCell className="text-muted-foreground">{r.state}</TableCell>
                  <TableCell className="text-muted-foreground">{r.district}</TableCell>
                  <TableCell className="text-muted-foreground">{r.area}</TableCell>
                  <TableCell className="font-mono text-xs">{r.poc_contact}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">{r.poc_email}</TableCell>
                  <TableCell><StatusBadge status={r.contract_status} /></TableCell>
                  <TableCell><SalesBadge yes={r.sales_status} /></TableCell>
                  <TableCell><StatusBadge status={r.tariff_status} testid={`tariff-status-${r.id}`} /></TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setModal({ open: true, id: r.id, create: false })} data-testid={`corporate-edit-button-${r.id}`}>
                        {isManagement ? <Pencil className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      {isManagement && (
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setToDelete(r)} data-testid={`corporate-delete-button-${r.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {modal.open && (
        <CorporateDetailModal
          open={modal.open}
          corporateId={modal.id}
          createMode={modal.create}
          properties={properties}
          onClose={() => setModal({ open: false, id: null, create: false })}
          onSaved={() => { load(); reloadNotifications(); }}
        />
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete corporate?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <strong>{toDelete?.company_name}</strong> and all its tariff and meeting data. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-button">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
